import React from 'react';
import { Box } from '@mui/material';

// Shared Components
import SolutionHero from '../components/SolutionHero';
import ServicesGrid from '../components/ServicesGrid';
import ProcessTimeline from '../components/ProcessTimeline';
import BenefitsSection from '../components/BenefitsSection';
import FAQAccordion from '../components/FAQAccordion';
import SolutionCTA from '../components/SolutionCTA';

// Icons
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HubIcon from '@mui/icons-material/Hub';
import AnalyticsIcon from '@mui/icons-material/Analytics';

const IntelligentAgents = () => {
    // Page Content
    const services = [
        {
            icon: <RecordVoiceOverIcon sx={{ fontSize: 28 }} />,
            title: "Voice AI Agents",
            description: "Human-like voice agents that handle inbound/outbound calls, schedule appointments, qualify leads, and resolve customer issues 24/7."
        },
        {
            icon: <SmartToyIcon sx={{ fontSize: 28 }} />,
            title: "Workflow Automation Agents",
            description: "Intelligent agents that automate complex business workflows, from data entry to document processing and multi-step approvals."
        },
        {
            icon: <SupportAgentIcon sx={{ fontSize: 28 }} />,
            title: "Customer Support Agents",
            description: "AI-powered support agents that resolve tier-1 tickets, escalate intelligently, and provide consistent 24/7 customer service."
        },
        {
            icon: <TrendingUpIcon sx={{ fontSize: 28 }} />,
            title: "Sales & Lead Qualification",
            description: "Agents that qualify leads, book demos, follow up with prospects, and nurture opportunities through your sales pipeline."
        },
        {
            icon: <HubIcon sx={{ fontSize: 28 }} />,
            title: "Multi-Agent Orchestration",
            description: "Complex systems where multiple specialized agents collaborate to handle sophisticated business processes."
        },
        {
            icon: <AnalyticsIcon sx={{ fontSize: 28 }} />,
            title: "Agent Monitoring & Analytics",
            description: "Real-time dashboards, conversation analytics, and performance metrics to optimize your agent fleet."
        }
    ];

    const processSteps = [
        {
            title: "Define Objectives",
            description: "Identify the tasks, conversations, and outcomes your agents need to handle."
        },
        {
            title: "Design Agent Flow",
            description: "Map conversation trees, decision logic, and integration points for seamless operation."
        },
        {
            title: "Build & Train",
            description: "Develop the agent using fine-tuned models, test against edge cases, and refine responses."
        },
        {
            title: "Deploy & Monitor",
            description: "Launch to production with real-time monitoring, analytics, and continuous improvement loops."
        }
    ];

    const benefits = [
        {
            title: "24/7 Availability",
            description: "Your agents never sleep, never take breaks, and handle peak loads without hiring. Scale customer touchpoints infinitely."
        },
        {
            title: "Human-Like Conversations",
            description: "Powered by advanced LLMs with custom personas, natural language understanding, and context-aware responses."
        },
        {
            title: "Seamless Integrations",
            description: "Connect to your CRM, ticketing system, calendar, phone system, and databases through robust API integrations."
        },
        {
            title: "Continuous Learning",
            description: "Agents improve over time with feedback loops, conversation analysis, and model fine-tuning based on real interactions."
        }
    ];

    const faqs = [
        {
            question: "How natural do the voice agents sound?",
            answer: "Our voice agents use state-of-the-art text-to-speech with natural intonation, pacing, and emotion. They're indistinguishable from human agents in most scenarios, with sub-second latency for real-time conversations."
        },
        {
            question: "Can agents handle complex, multi-turn conversations?",
            answer: "Yes. Our agents maintain full conversation context, handle interruptions, clarify ambiguous requests, and navigate complex decision trees while staying on-brand and goal-oriented."
        },
        {
            question: "What systems can agents integrate with?",
            answer: "We integrate with major CRMs (Salesforce, HubSpot), ticketing systems (Zendesk, Freshdesk), calendars (Google, Outlook), phone systems (Twilio, Vonage), and custom APIs."
        },
        {
            question: "How do you ensure data security for voice calls?",
            answer: "All calls are encrypted in transit and at rest. We support on-premise deployment for sensitive industries, SOC2 compliance, and HIPAA-compliant configurations for healthcare."
        },
        {
            question: "How long does it take to deploy an agent?",
            answer: "Simple agents can be deployed in 2-4 weeks. Complex multi-agent systems with deep integrations typically take 6-12 weeks depending on scope and requirements."
        }
    ];

    const ctaStats = [
        { value: "90%", label: "Call Resolution Rate" },
        { value: "<1s", label: "Response Latency" },
        { value: "24/7", label: "Always Available" },
        { value: "60%", label: "Cost Reduction" }
    ];

    return (
        <Box sx={{ bgcolor: '#030014', minHeight: '100vh' }}>
            {/* Hero Section */}
            <SolutionHero
                headline="AI Agents That Work"
                highlightedText="24/7 for You"
                subheadline="Voice agents, workflow automation, and conversational AI that handle complex tasks while you focus on growth. Human-like, always available, infinitely scalable."
                primaryCTA="See Agents in Action"
                primaryCTALink="/demo"
                secondaryCTA="Build Your Agent"
                secondaryCTALink="#process"
            />

            {/* Services Grid */}
            <ServicesGrid
                title="Intelligent Agent Solutions"
                subtitle="Agent Capabilities"
                services={services}
            />

            {/* Process Timeline */}
            <ProcessTimeline
                title="From Concept to Production"
                subtitle="How We Build Agents"
                steps={processSteps}
            />

            {/* Benefits Section */}
            <BenefitsSection
                title="Why Our Agents Excel"
                subtitle="Agent Advantages"
                benefits={benefits}
            />

            {/* FAQ Section */}
            <FAQAccordion
                title="Frequently Asked Questions"
                subtitle="Common Questions"
                faqs={faqs}
            />

            {/* CTA Section */}
            <SolutionCTA
                headline="Ready to Deploy Your AI Workforce?"
                subheadline="Stop scaling with headcount. Start scaling with intelligent agents that work around the clock."
                primaryCTA="Schedule a Demo"
                primaryCTALink="/demo"
                secondaryCTA="Talk to an Expert"
                secondaryCTALink="/contact-us"
                stats={ctaStats}
            />
        </Box>
    );
};

export default IntelligentAgents;
